import {i18n} from 'src/i18n';
import AuthCurrentTenant from 'src/modules/auth/authCurrentTenant';
import selectors from 'src/modules/auth/authSelectors';
import service from 'src/modules/auth/authService';
import {AuthToken} from 'src/modules/auth/authToken';
import googleCalendarService from 'src/modules/auth/googleCalendarService';
import Errors from 'src/modules/shared/error/errors';
import {getHistory} from 'src/modules/history';
import Message from 'src/view/shared/message';
import dataListActions from '../data/list/dataListActions';
import {tenantSubdomain} from '../tenant/tenantSubdomain';
import {
  clearLatestSearchesLocalCache,
  computeLatestSearchesAccessFingerprint,
  scheduleSyncLatestSearchesClearToServer,
  syncLatestSearchesAccessFingerprintAndMaybeInvalidate,
} from 'src/modules/auth/authLatestSearchesAccess';
import {persistCurrentDivisionId} from 'src/modules/auth/authCurrentDivisionStorage';
import {divisionScopeRefetchForPathname} from 'src/modules/auth/divisionScopeRefetchForPathname';
import {getSelectableDivisionIds} from 'src/modules/auth/currentDivisionScope';
import {getSyntheticTenantAccessContext} from 'src/modules/auth/tenantAccessContextDefaults';

type TenantAccessContextPayload = {
  tenantAccessContext: any;
  tenantAccessContextLoadStatus: 'loaded' | 'failed' | null;
};

/**
 * Hidrata tenant-context. Si falla el endpoint, contexto sintético + `failed` mantiene permisividad
 * acordada para clientes sin backend nuevo (no dejar `null` ambiguo). Ver comentarios en
 * `tenantAccessContextDefaults.ts` y `tareaPdfAccess.ts`.
 */
async function fetchTenantAccessContextPayload(currentUser: any): Promise<TenantAccessContextPayload> {
  if (!currentUser) {
    return {tenantAccessContext: null, tenantAccessContextLoadStatus: null};
  }
  const tenantId = AuthCurrentTenant.get();
  if (!tenantId) {
    return {tenantAccessContext: null, tenantAccessContextLoadStatus: null};
  }
  try {
    const ctx = await service.fetchTenantAccessContext(tenantId);
    return {
      tenantAccessContext: ctx,
      tenantAccessContextLoadStatus: 'loaded',
    };
  } catch (_e) {
    return {
      tenantAccessContext: getSyntheticTenantAccessContext(),
      tenantAccessContextLoadStatus: 'failed',
    };
  }
}

const prefix = 'AUTH';

const authActions = {
  ERROR_MESSAGE_CLEARED: `${prefix}_ERROR_MESSAGE_CLEARED`,

  AUTH_INIT_SUCCESS: `${prefix}_INIT_SUCCESS`,
  AUTH_INIT_ERROR: `${prefix}_INIT_ERROR`,

  AUTH_START: `${prefix}_START`,
  AUTH_SUCCESS: `${prefix}_SUCCESS`,
  AUTH_ERROR: `${prefix}_ERROR`,

  UPDATE_PROFILE_START: `${prefix}_UPDATE_PROFILE_START`,
  UPDATE_PROFILE_SUCCESS: `${prefix}_UPDATE_PROFILE_SUCCESS`,
  UPDATE_PROFILE_ERROR: `${prefix}_UPDATE_PROFILE_ERROR`,

  PASSWORD_CHANGE_START: `${prefix}_PASSWORD_CHANGE_START`,
  PASSWORD_CHANGE_SUCCESS: `${prefix}_PASSWORD_CHANGE_SUCCESS`,
  PASSWORD_CHANGE_ERROR: `${prefix}_PASSWORD_CHANGE_ERROR`,

  CURRENT_USER_REFRESH_START: `${prefix}_CURRENT_USER_REFRESH_START`,
  CURRENT_USER_REFRESH_SUCCESS: `${prefix}_CURRENT_USER_REFRESH_SUCCESS`,
  CURRENT_USER_REFRESH_ERROR: `${prefix}_CURRENT_USER_REFRESH_ERROR`,

  PASSWORD_RESET_EMAIL_START: `${prefix}_PASSWORD_RESET_EMAIL_START`,
  PASSWORD_RESET_EMAIL_SUCCESS: `${prefix}_PASSWORD_RESET_EMAIL_SUCCESS`,
  PASSWORD_RESET_EMAIL_ERROR: `${prefix}_PASSWORD_RESET_EMAIL_ERROR`,

  PASSWORD_RESET_START: `${prefix}_PASSWORD_RESET_START`,
  PASSWORD_RESET_SUCCESS: `${prefix}_PASSWORD_RESET_SUCCESS`,
  PASSWORD_RESET_ERROR: `${prefix}_PASSWORD_RESET_ERROR`,

  EMAIL_VERIFY_START: `${prefix}_EMAIL_VERIFY_START`,
  EMAIL_VERIFY_SUCCESS: `${prefix}_EMAIL_VERIFY_SUCCESS`,
  EMAIL_VERIFY_ERROR: `${prefix}_EMAIL_VERIFY_ERROR`,

  EMAIL_CONFIRMATION_START: `${prefix}_EMAIL_CONFIRMATION_START`,
  EMAIL_CONFIRMATION_SUCCESS: `${prefix}_EMAIL_CONFIRMATION_SUCCESS`,
  EMAIL_CONFIRMATION_ERROR: `${prefix}_EMAIL_CONFIRMATION_ERROR`,

  // Google Calendar actions
  GOOGLE_CALENDAR_CONNECT_START: `${prefix}_GOOGLE_CALENDAR_CONNECT_START`,
  GOOGLE_CALENDAR_CONNECT_SUCCESS: `${prefix}_GOOGLE_CALENDAR_CONNECT_SUCCESS`,
  GOOGLE_CALENDAR_CONNECT_ERROR: `${prefix}_GOOGLE_CALENDAR_CONNECT_ERROR`,

  GOOGLE_CALENDAR_DISCONNECT_START: `${prefix}_GOOGLE_CALENDAR_DISCONNECT_START`,
  GOOGLE_CALENDAR_DISCONNECT_SUCCESS: `${prefix}_GOOGLE_CALENDAR_DISCONNECT_SUCCESS`,
  GOOGLE_CALENDAR_DISCONNECT_ERROR: `${prefix}_GOOGLE_CALENDAR_DISCONNECT_ERROR`,

  TENANT_ACCESS_CONTEXT_SUCCESS: `${prefix}_TENANT_ACCESS_CONTEXT_SUCCESS`,
  TENANT_ACCESS_CONTEXT_FAILED: `${prefix}_TENANT_ACCESS_CONTEXT_FAILED`,
  TENANT_ACCESS_CONTEXT_CLEARED: `${prefix}_TENANT_ACCESS_CONTEXT_CLEARED`,
  MERGE_TENANT_USER_DATA: `${prefix}_MERGE_TENANT_USER_DATA`,
  CURRENT_DIVISION_SET: `${prefix}_CURRENT_DIVISION_SET`,

  doMergeTenantUserData: (dataFragment: Record<string, any>) => (dispatch) => {
    dispatch({
      type: authActions.MERGE_TENANT_USER_DATA,
      payload: dataFragment,
    });
  },

  doSetCurrentDivision: (divisionId: string | null) => (dispatch, getState) => {
    const ctx = getState()?.auth?.tenantAccessContext;
    const allowed = getSelectableDivisionIds(ctx);
    if (divisionId != null && !allowed.includes(divisionId)) {
      return;
    }
    const prev = getState()?.auth?.currentDivisionId ?? null;
    const next = divisionId ?? null;
    if (prev === next) {
      return;
    }
    persistCurrentDivisionId(divisionId);
    dispatch({
      type: authActions.CURRENT_DIVISION_SET,
      payload: {currentDivisionId: divisionId},
    });
    clearLatestSearchesLocalCache();
    scheduleSyncLatestSearchesClearToServer();
    const path = getState()?.router?.location?.pathname;
    divisionScopeRefetchForPathname(dispatch, getState, typeof path === 'string' ? path : '');
  },

  doClearErrorMessage() {
    return {
      type: authActions.ERROR_MESSAGE_CLEARED,
    };
  },

  doFetchTenantAccessContext: () => async (dispatch, getState) => {
    const tenantId = AuthCurrentTenant.get();
    const token = AuthToken.get();
    if (!token || !tenantId) {
      dispatch({type: authActions.TENANT_ACCESS_CONTEXT_CLEARED});
      return;
    }
    try {
      const ctx = await service.fetchTenantAccessContext(tenantId);
      const currentUser = getState()?.auth?.currentUser;
      const nextFp = computeLatestSearchesAccessFingerprint(currentUser, tenantId, ctx);
      syncLatestSearchesAccessFingerprintAndMaybeInvalidate(nextFp);
      dispatch({
        type: authActions.TENANT_ACCESS_CONTEXT_SUCCESS,
        payload: {context: ctx, loadStatus: 'loaded' as const},
      });
    } catch (_e) {
      dispatch({type: authActions.TENANT_ACCESS_CONTEXT_FAILED});
    }
  },

  doSendEmailConfirmation: () => async (dispatch, getState) => {
    try {
      dispatch({type: authActions.EMAIL_CONFIRMATION_START});
      await service.sendEmailVerification();
      Message.success(i18n('auth.verificationEmailSuccess'));
      dispatch({
        type: authActions.EMAIL_CONFIRMATION_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);
      dispatch({type: authActions.EMAIL_CONFIRMATION_ERROR});
    }
  },

  doSendPasswordResetEmail: (email) => async (dispatch) => {
    try {
      dispatch({
        type: authActions.PASSWORD_RESET_EMAIL_START,
      });
      await service.sendPasswordResetEmail(email);
      Message.success(i18n('auth.passwordResetEmailSuccess'));
      dispatch({
        type: authActions.PASSWORD_RESET_EMAIL_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);
      dispatch({
        type: authActions.PASSWORD_RESET_EMAIL_ERROR,
      });
    }
  },

  doRegisterEmailAndPassword: (email, password) => async (dispatch) => {
    try {
      dispatch({type: authActions.AUTH_START});

      const token = await service.registerWithEmailAndPassword(email, password);

      AuthToken.set(token, true);

      const currentUser = await service.fetchMe();
      const tcp = await fetchTenantAccessContextPayload(currentUser);

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: {
          currentUser,
          tenantAccessContext: tcp.tenantAccessContext,
          tenantAccessContextLoadStatus: tcp.tenantAccessContextLoadStatus,
        },
      });
    } catch (error) {
      await service.signout();

      if (Errors.errorCode(error) !== 400) {
        Errors.handle(error);
      }

      dispatch({
        type: authActions.AUTH_ERROR,
        payload: Errors.selectMessage(error),
      });
    }
  },

  doSigninWithEmailAndPassword: (email, password, rememberMe) => async (dispatch) => {
    try {
      dispatch({type: authActions.AUTH_START});

      let currentUser = null;

      const token = await service.signinWithEmailAndPassword(email, password);

      AuthToken.set(token, rememberMe);
      currentUser = await service.fetchMe();
      const tcp = await fetchTenantAccessContextPayload(currentUser);

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: {
          currentUser,
          tenantAccessContext: tcp.tenantAccessContext,
          tenantAccessContextLoadStatus: tcp.tenantAccessContextLoadStatus,
        },
      });
    } catch (error) {
      await service.signout();

      if (Errors.errorCode(error) !== 400) {
        Errors.handle(error);
      }

      dispatch({
        type: authActions.AUTH_ERROR,
        payload: Errors.selectMessage(error),
      });
    }
  },

  doSignout: () => async (dispatch) => {
    try {
      dispatch({type: authActions.AUTH_START});
      await service.signout();

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: {
          currentUser: null,
        },
      });
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.AUTH_ERROR,
      });
    }
  },

  doInit: () => async (dispatch) => {
    try {
      const token = AuthToken.get();
      let currentUser: any = null;

      if (token) {
        currentUser = await service.fetchMe();
      }

      const tcp = await fetchTenantAccessContextPayload(currentUser);

      const tenantId = AuthCurrentTenant.get();
      let latestSearchesHydrateSkipped = false;
      if (tenantId && currentUser?.tenants) {
        const nextFp = computeLatestSearchesAccessFingerprint(
          currentUser,
          tenantId,
          tcp.tenantAccessContext,
        );
        latestSearchesHydrateSkipped = syncLatestSearchesAccessFingerprintAndMaybeInvalidate(nextFp);
      } else {
        syncLatestSearchesAccessFingerprintAndMaybeInvalidate('');
      }

      if (token && tenantId && currentUser?.tenants && !latestSearchesHydrateSkipped) {
        const tenantUser = currentUser.tenants.find((tu) => tu.tenant.id === tenantId);
        const latestSearches = tenantUser?.data?.latestSearches;
        if (latestSearches) {
          AuthCurrentTenant.setItem('latestSearches', latestSearches);
        }
      }

      dispatch({
        type: authActions.AUTH_INIT_SUCCESS,
        payload: {
          currentUser,
          tenantAccessContext: tcp.tenantAccessContext,
          tenantAccessContextLoadStatus: tcp.tenantAccessContextLoadStatus,
        },
      });
    } catch (error) {
      service.signout();
      Errors.handle(error);

      dispatch({
        type: authActions.AUTH_INIT_ERROR,
        payload: error,
      });
    }
  },

  updateLatestSearches: (entity, newSearches) => async (dispatch) => {
    try {
      const result = await service.updateLatestSearches(entity, newSearches);
      if (!result?.id) {
        return;
      }
      AuthCurrentTenant.setItem('latestSearches', result.latestSearches);
    } catch (error) {
      Errors.handle(error);
    }
  },

  doRefreshCurrentUser: () => async (dispatch) => {
    try {
      dispatch({
        type: authActions.CURRENT_USER_REFRESH_START,
      });

      let currentUser = null;
      const token = AuthToken.get();

      if (token) {
        currentUser = await service.fetchMe();
      }

      const tcp = await fetchTenantAccessContextPayload(currentUser);

      const tenantId = AuthCurrentTenant.get();
      if (tenantId && currentUser?.tenants) {
        const nextFp = computeLatestSearchesAccessFingerprint(
          currentUser,
          tenantId,
          tcp.tenantAccessContext,
        );
        syncLatestSearchesAccessFingerprintAndMaybeInvalidate(nextFp);
      } else {
        syncLatestSearchesAccessFingerprintAndMaybeInvalidate('');
      }

      dispatch({
        type: authActions.CURRENT_USER_REFRESH_SUCCESS,
        payload: {
          currentUser,
          tenantAccessContext: tcp.tenantAccessContext,
          tenantAccessContextLoadStatus: tcp.tenantAccessContextLoadStatus,
        },
      });
    } catch (error) {
      service.signout();
      Errors.handle(error);

      dispatch({
        type: authActions.CURRENT_USER_REFRESH_ERROR,
        payload: error,
      });
    }
  },

  doUpdateProfile: (data) => async (dispatch) => {
    try {
      dispatch({
        type: authActions.UPDATE_PROFILE_START,
      });

      await service.updateProfile(data);

      dispatch({
        type: authActions.UPDATE_PROFILE_SUCCESS,
      });
      await dispatch(authActions.doRefreshCurrentUser());
      Message.success(i18n('auth.profile.success'));
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.UPDATE_PROFILE_ERROR,
      });
    }
  },

  doChangePassword: (oldPassword, newPassword) => async (dispatch) => {
    try {
      dispatch({
        type: authActions.PASSWORD_CHANGE_START,
      });

      await service.changePassword(oldPassword, newPassword);

      dispatch({
        type: authActions.PASSWORD_CHANGE_SUCCESS,
      });
      await dispatch(authActions.doRefreshCurrentUser());
      Message.success(i18n('auth.passwordChange.success'));
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.PASSWORD_CHANGE_ERROR,
      });
    }
  },

  doVerifyEmail: (token) => async (dispatch, getState) => {
    try {
      const isLoading = selectors.selectLoadingVerifyEmail(getState());

      if (isLoading) {
        return;
      }

      dispatch({
        type: authActions.EMAIL_VERIFY_START,
      });

      await service.verifyEmail(token);

      await dispatch(authActions.doRefreshCurrentUser());

      dispatch({
        type: authActions.EMAIL_VERIFY_SUCCESS,
      });

      getHistory().push('/');
    } catch (error) {
      Errors.showMessage(error);
      dispatch({
        type: authActions.EMAIL_VERIFY_ERROR,
      });
      getHistory().push('/');
    }
  },

  doResetPassword: (token, password) => async (dispatch) => {
    try {
      dispatch({
        type: authActions.PASSWORD_RESET_START,
      });

      await service.passwordReset(token, password);

      Message.success(i18n('auth.passwordResetSuccess'));
      dispatch({
        type: authActions.PASSWORD_RESET_SUCCESS,
      });
      getHistory().push('/');
    } catch (error) {
      Errors.handle(error);

      dispatch({
        type: authActions.PASSWORD_RESET_ERROR,
      });

      dispatch(authActions.doSignout());
      getHistory().push('/');
    }
  },

  doSelectTenant: (tenant) => async (dispatch) => {
    await dispatch({type: 'RESET_STATE'});
    dispatch(dataListActions.doSelectEntity(null));
    if (tenantSubdomain.isEnabled) {
      tenantSubdomain.redirectAuthenticatedTo(tenant.url);
      return;
    }

    AuthCurrentTenant.set(tenant);
    await dispatch(authActions.doRefreshCurrentUser());
    getHistory().push('/');
  },

  doSigninWithGoogle: (googleToken) => async (dispatch) => {
    try {
      dispatch({type: authActions.AUTH_START});

      let currentUser = null;

      const token = await service.signinWithGoogle(googleToken);

      AuthToken.set(token, true);
      currentUser = await service.fetchMe();
      const tcp = await fetchTenantAccessContextPayload(currentUser);

      dispatch({
        type: authActions.AUTH_SUCCESS,
        payload: {
          currentUser,
          tenantAccessContext: tcp.tenantAccessContext,
          tenantAccessContextLoadStatus: tcp.tenantAccessContextLoadStatus,
        },
      });
    } catch (error) {
      await service.signout();

      if (Errors.errorCode(error) !== 400) {
        Errors.handle(error);
      }

      dispatch({
        type: authActions.AUTH_ERROR,
        payload: Errors.selectMessage(error),
      });

      throw error;
    }
  },

  // Google Calendar Actions
  doConnectGoogleCalendar: () => async (dispatch) => {
    try {
      dispatch({type: authActions.GOOGLE_CALENDAR_CONNECT_START});

      const result = await googleCalendarService.getConnectUrl();

      // Redirect to Google OAuth URL
      window.location.href = result.authUrl;
    } catch (error) {
      Errors.handle(error);
      dispatch({
        type: authActions.GOOGLE_CALENDAR_CONNECT_ERROR,
        payload: Errors.selectMessage(error),
      });
    }
  },

  doHandleGoogleCalendarCallback: (code: string, state?: string) => async (dispatch) => {
    try {
      dispatch({type: authActions.GOOGLE_CALENDAR_CONNECT_START});

      await googleCalendarService.handleCallback(code, state);

      // Refresh current user to get updated Google tokens
      await dispatch(authActions.doRefreshCurrentUser());

      Message.success(i18n('auth.googleCalendar.connectSuccess'));

      dispatch({
        type: authActions.GOOGLE_CALENDAR_CONNECT_SUCCESS,
      });

      // Redirect back to profile page where the user initiated the connection
      getHistory().push('/profile');
    } catch (error) {
      Errors.handle(error);
      dispatch({
        type: authActions.GOOGLE_CALENDAR_CONNECT_ERROR,
        payload: Errors.selectMessage(error),
      });

      // Redirect to error page or back to settings
      getHistory().push('/');
    }
  },

  doDisconnectGoogleCalendar: () => async (dispatch) => {
    try {
      dispatch({type: authActions.GOOGLE_CALENDAR_DISCONNECT_START});

      await googleCalendarService.disconnect();

      // Refresh current user to get updated status
      await dispatch(authActions.doRefreshCurrentUser());

      Message.success(i18n('auth.googleCalendar.disconnectSuccess'));

      dispatch({
        type: authActions.GOOGLE_CALENDAR_DISCONNECT_SUCCESS,
      });
    } catch (error) {
      Errors.handle(error);
      dispatch({
        type: authActions.GOOGLE_CALENDAR_DISCONNECT_ERROR,
        payload: Errors.selectMessage(error),
      });
    }
  },
};

export default authActions;
